import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import Button from '@mui/material/Button';

import TextFieldWithLoadingButton from '@/component/common/textfield-with-loading-button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { SFPalette } from '@/lib/color';
import SendMailDrawer from './send-mail-drawer';
import PasswordTextField from '@/component/common/password-textfield';
import { checkEmail } from '@/api/server/user/read';
import { toast } from 'react-toastify';
import ErrorRes from '@/api/server/ErrorRes';
import Box from '@mui/material/Box';
import BottomFix from '../common/bottom-fix';

type FormDataType = {
  email: string;
  password: string;
  confirmPassword: string;
};
const schema = Joi.object({
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required(),
  password: Joi.string()
    .pattern(new RegExp('^(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,16}$'))
    .required(),
  confirmPassword: Joi.string()
    .valid(Joi.ref('password'))
    .required()
    .messages({ 'any.only': '비밀번호가 일치하지 않습니다.' }),
});

export default function SignUpForm() {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormDataType>({
    resolver: joiResolver(schema),
    mode: 'onChange',
  });
  const [emailCheck, setEmailCheck] = useState(200);

  const [openSendMailDrawer, setOpenSendMailDrawer] = useState(false);

  const onSubmit = async (data: FormDataType) => {
    setOpenSendMailDrawer(true);
    return data;
  };

  const password = watch('password');
  const email = watch('email');
  const confirmPassword = watch('confirmPassword');

  const isReadyToSubmit =
    !errors.email && !errors.password && !errors.confirmPassword && email && password && confirmPassword;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack rowGap={3.5} mb={5}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <Typography variant="body1" mb={1}>
                  <strong>이메일</strong>
                </Typography>
                <TextFieldWithLoadingButton
                  {...field}
                  fullWidth
                  type="email"
                  error={!!errors.email}
                  buttonText="중복확인"
                  placeholder="이메일 입력"
                  buttonDisabled={!!errors.email || !email}
                  onClickButton={async () => {
                    const re = await checkEmail(email);
                    if (re.ret_code !== 200) {
                      setEmailCheck(re.ret_code);
                      ErrorRes({ code: re.ret_code, msg: re.msg, isBack: false });
                      return;
                    } else if (re.ret_code === 200) {
                      toast.success('사용가능한 아이디(이메일) 입니다.');
                    }
                  }}
                  helperText={
                    !email
                      ? ''
                      : errors.email
                      ? '사용 불가능한 아이디(이메일)입니다.'
                      : emailCheck === 200
                      ? '중복확인 버튼을 눌러주세요.'
                      : '이미 등록된 사용자 이메일입니다.'
                    // TODO 확인해야 함
                    // : '사용가능한 아이디(이메일) 입니다.'
                  }
                  FormHelperTextProps={{
                    style: {
                      color: errors.email ? 'red' : email ? SFPalette.shuffleColor2 : SFPalette.lightGrey2,
                    },
                  }}
                />
              </div>
            )}
          />
          <Box pt={2} />
          <Controller
            name="password"
            control={control}
            defaultValue=""
            render={({ field }) => {
              return (
                <div>
                  <Typography variant="body1" mb={1}>
                    <strong>비밀번호</strong>
                  </Typography>
                  <PasswordTextField
                    {...field}
                    fullWidth
                    placeholder="비밀번호 입력"
                    error={!!errors.password}
                    helperText={'영문 / 숫자 / 특수문자 / 8~16자'}
                  />
                </div>
              );
            }}
          />
           <Box pt={2} />
          <Controller
            name="confirmPassword"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <div>
                <Typography variant="body1" mb={1}>
                  <strong>비밀번호 확인</strong>
                </Typography>
                <PasswordTextField
                  {...field}
                  fullWidth
                  placeholder="비밀번호 재입력"
                  error={!!errors.confirmPassword}
                  helperText={
                    !confirmPassword
                      ? ''
                      : errors.confirmPassword
                      ? '비밀번호가 일치하지 않습니다.'
                      : '비밀번호가 일치합니다.'
                  }
                />
              </div>
            )}
          />
        </Stack>

        <BottomFix>
          <Button
            sx={{
              my: 2,
            }}
            variant="contained"
            type="submit"
            disabled={!isReadyToSubmit}
            size="large"
            fullWidth
          >
            확인
          </Button>
        </BottomFix>
      </form>
      <SendMailDrawer
        open={openSendMailDrawer}
        email={email}
        password={password}
        type="create"
        onClose={() => setOpenSendMailDrawer(false)}
      />
    </>
  );
}
